import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Lock, Eye, EyeOff, Loader2, ShieldCheck, AlertTriangle, CheckCircle } from 'lucide-react';

// Standalone sifre sifirlama sayfasi (Commit 6/6).
// /admin-reset-password?token=... URL'i ile acilir; backend reset emailindeki link buraya gelir.
// Token URL'den alinir; backend'e POST /api/admin-reset-password ile gonderilir.
// 2FA secret etkilenmez — sadece sifre degisir.
const API = process.env.REACT_APP_API_URL || '/api';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [language, setLanguage] = useState('tr');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);

  const content = {
    tr: {
      title: 'Şifre Sıfırlama',
      subtitle: 'Yeni şifrenizi belirleyin',
      newPasswordLabel: 'Yeni Şifre',
      newPasswordPlaceholder: 'Yeni şifrenizi girin',
      confirmPasswordLabel: 'Yeni Şifre (Tekrar)',
      confirmPasswordPlaceholder: 'Yeni şifrenizi tekrar girin',
      passwordRules: 'Şifre kuralları',
      passwordRule1: 'En az 12 karakter',
      passwordRule2: 'En az bir büyük harf (A-Z)',
      passwordRule3: 'En az bir küçük harf (a-z)',
      passwordRule4: 'En az bir rakam (0-9)',
      passwordRule5: 'En az bir özel karakter (!@#$% vb.)',
      submitButton: 'Şifreyi Değiştir',
      submitting: 'Değiştiriliyor...',
      successTitle: 'Şifreniz Başarıyla Değiştirildi',
      successMessage: 'Yeni şifreniz ile giriş yapabilirsiniz.',
      goToLogin: 'Giriş Sayfasına Git',
      passwordMismatch: 'Yeni şifreler eşleşmiyor',
      passwordTooShort: 'Şifre en az 12 karakter olmalı',
      passwordWeak: 'Şifre güvenlik kurallarını karşılamıyor',
      tokenMissing: 'Geçersiz reset linki. Lütfen tekrar şifre sıfırlama talep edin.',
      tokenExpired: 'Reset linki geçersiz veya süresi dolmuş. Lütfen tekrar talep edin.',
      genericError: 'Şifre değiştirilemedi. Tekrar deneyin.',
      backToLogin: '← Giriş sayfasına dön',
      invalidLinkTitle: 'Geçersiz Link',
    },
    en: {
      title: 'Password Reset',
      subtitle: 'Set your new password',
      newPasswordLabel: 'New Password',
      newPasswordPlaceholder: 'Enter your new password',
      confirmPasswordLabel: 'New Password (Confirm)',
      confirmPasswordPlaceholder: 'Confirm your new password',
      passwordRules: 'Password rules',
      passwordRule1: 'At least 12 characters',
      passwordRule2: 'At least one uppercase letter (A-Z)',
      passwordRule3: 'At least one lowercase letter (a-z)',
      passwordRule4: 'At least one digit (0-9)',
      passwordRule5: 'At least one special character (!@#$% etc.)',
      submitButton: 'Change Password',
      submitting: 'Changing...',
      successTitle: 'Password Changed Successfully',
      successMessage: 'You can now sign in with your new password.',
      goToLogin: 'Go to Login Page',
      passwordMismatch: 'New passwords do not match',
      passwordTooShort: 'Password must be at least 12 characters',
      passwordWeak: 'Password does not meet security rules',
      tokenMissing: 'Invalid reset link. Please request a new password reset.',
      tokenExpired: 'Reset link is invalid or expired. Please request a new one.',
      genericError: 'Could not change password. Try again.',
      backToLogin: '← Back to login',
      invalidLinkTitle: 'Invalid Link',
    },
  };
  const t = content[language];

  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      setError(content[language].tokenMissing);
    }
  }, [token, language]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (newPassword !== confirmPassword) {
      setError(t.passwordMismatch);
      setLoading(false);
      return;
    }

    if (newPassword.length < 12) {
      setError(t.passwordTooShort);
      setLoading(false);
      return;
    }

    if (
      !/[A-Z]/.test(newPassword) ||
      !/[a-z]/.test(newPassword) ||
      !/[0-9]/.test(newPassword) ||
      !/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword)
    ) {
      setError(t.passwordWeak);
      setLoading(false);
      return;
    }

    try {
      await axios.post(`${API}/admin-reset-password`, {
        token,
        new_password: newPassword,
      });

      setSuccess(true);
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      if (err.response?.status === 401) {
        setError(t.tokenExpired);
        setTokenValid(false);
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError(t.genericError);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-gray-900 rounded-xl border-2 border-[#FFDD59] p-8">
        <div className="flex justify-end mb-4">
          <button
            onClick={() => setLanguage(language === 'tr' ? 'en' : 'tr')}
            className="text-xs text-gray-400 hover:text-white"
          >
            {language === 'tr' ? 'EN' : 'TR'}
          </button>
        </div>

        {success ? (
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500/20 rounded-full mb-4">
              <CheckCircle className="text-green-400" size={32} />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">{t.successTitle}</h1>
            <p className="text-gray-400 mb-6">{t.successMessage}</p>
            <button
              onClick={() => navigate('/adminboraorkun')}
              className="w-full py-3 bg-[#FFDD59] text-black font-bold rounded-lg hover:bg-yellow-500 transition"
              data-testid="reset-success-go-to-login"
            >
              {t.goToLogin}
            </button>
          </div>
        ) : !tokenValid ? (
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-500/20 rounded-full mb-4">
              <AlertTriangle className="text-red-400" size={32} />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">{t.invalidLinkTitle}</h1>
            <p className="text-gray-400 mb-6">{error}</p>
            <button
              onClick={() => navigate('/adminboraorkun')}
              className="w-full py-3 bg-gray-700 text-white font-bold rounded-lg hover:bg-gray-600 transition"
            >
              {t.backToLogin}
            </button>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FFDD59]/20 rounded-full mb-4">
                <ShieldCheck className="text-[#FFDD59]" size={32} />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">{t.title}</h1>
              <p className="text-gray-400 text-sm">{t.subtitle}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">{t.newPasswordLabel}</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder={t.newPasswordPlaceholder}
                    required
                    autoComplete="new-password"
                    autoFocus
                    className="w-full pl-10 pr-12 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#FFDD59]"
                    data-testid="reset-new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">{t.confirmPasswordLabel}</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t.confirmPasswordPlaceholder}
                    required
                    autoComplete="new-password"
                    className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#FFDD59]"
                    data-testid="reset-confirm-password-input"
                  />
                </div>
              </div>

              <div className="p-3 bg-gray-800/50 rounded-lg text-xs text-gray-400">
                <p className="font-semibold mb-1">{t.passwordRules}:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>{t.passwordRule1}</li>
                  <li>{t.passwordRule2}</li>
                  <li>{t.passwordRule3}</li>
                  <li>{t.passwordRule4}</li>
                  <li>{t.passwordRule5}</li>
                </ul>
              </div>

              {error && (
                <div
                  className="p-3 bg-red-500/20 border border-red-500 text-red-300 rounded-lg text-sm"
                  data-testid="reset-error"
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !newPassword || !confirmPassword}
                className="w-full py-3 bg-[#FFDD59] text-black font-bold rounded-lg hover:bg-yellow-500 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="reset-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    {t.submitting}
                  </>
                ) : (
                  t.submitButton
                )}
              </button>

              <button
                type="button"
                onClick={() => navigate('/adminboraorkun')}
                className="w-full py-2 text-gray-400 hover:text-white transition text-sm"
              >
                {t.backToLogin}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
